import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { PRIMARY, TEXT_DARK, TEXT_MUTED, BORDER_LIGHT, BG_CARD, BG_SURFACE } from '../components/legacy-ball/table-management/designTokens';

interface MemberAttending {
  id: string;
  fullName: string;
  ticketNumber: string | null;
  chapterAffiliation: string | null;
  tableName: string | null;
  seatNumber: number | null;
}

type SortField = 'name' | 'table';
type SortDir = 'asc' | 'desc';

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '900px', margin: '0 auto' },
  header: { display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' },
  title: { color: PRIMARY, fontSize: '1.5rem', fontWeight: 700, margin: 0 },
  backBtn: {
    background: 'none',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    padding: '6px 12px',
    fontSize: '0.85rem',
    cursor: 'pointer',
    color: TEXT_DARK,
  },
  controls: { display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' },
  select: {
    padding: '6px 10px',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    fontSize: '0.85rem',
    backgroundColor: BG_SURFACE,
    color: TEXT_DARK,
    outline: 'none',
  },
  controlLabel: { fontSize: '0.8rem', color: TEXT_MUTED, fontWeight: 600 },
  count: { fontSize: '0.9rem', color: TEXT_MUTED, marginBottom: '1rem' },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.85rem', backgroundColor: BG_CARD, borderRadius: '8px', overflow: 'hidden' },
  th: { textAlign: 'left' as const, padding: '10px 12px', borderBottom: `2px solid ${BORDER_LIGHT}`, fontWeight: 600, color: TEXT_DARK, fontSize: '0.8rem', cursor: 'pointer', userSelect: 'none' as const },
  td: { padding: '8px 12px', borderBottom: `1px solid ${BORDER_LIGHT}`, color: TEXT_DARK },
  loading: { textAlign: 'center' as const, padding: '2rem', color: TEXT_MUTED },
  error: { color: '#d32f2f', padding: '1rem', backgroundColor: '#fdecea', borderRadius: '6px', fontSize: '0.85rem' },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 600 },
};

export function ChapterMembersAttendingPage() {
  const navigate = useNavigate();
  const [members, setMembers] = useState<MemberAttending[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [tableFilter, setTableFilter] = useState<string>('');

  useEffect(() => {
    async function load() {
      try {
        const data = await apiClient<MemberAttending[]>('/dashboard/chapter-members-attending');
        setMembers(data);
      } catch (err: any) {
        setError(err?.message || 'Failed to load chapter members');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  // Derive unique table names for filter dropdown
  const tableNames = useMemo(() => {
    const names = new Set<string>();
    for (const m of members) {
      if (m.tableName) names.add(m.tableName);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [members]);

  // Filter + sort
  const displayed = useMemo(() => {
    let list = members;

    // Filter by table
    if (tableFilter) {
      if (tableFilter === '__unassigned__') {
        list = list.filter((m) => !m.tableName);
      } else {
        list = list.filter((m) => m.tableName === tableFilter);
      }
    }

    // Sort
    list = [...list].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'name') {
        cmp = (a.fullName || '').localeCompare(b.fullName || '');
      } else {
        // Sort by table name (numeric-aware), unassigned last
        const aTable = a.tableName || '\uffff';
        const bTable = b.tableName || '\uffff';
        cmp = aTable.localeCompare(bTable, undefined, { numeric: true });
        if (cmp === 0) cmp = (a.seatNumber || 999) - (b.seatNumber || 999);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return list;
  }, [members, sortField, sortDir, tableFilter]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortIndicator = (field: SortField) => {
    if (sortField !== field) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  if (loading) return <div style={styles.loading}>Loading chapter members...</div>;
  if (error) return <div style={styles.error}>{error}</div>;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={() => navigate('/dashboard')} aria-label="Back to dashboard">
          ← Dashboard
        </button>
        <h1 style={styles.title}>KDU GA Members Attending Legacy Ball</h1>
      </div>

      {/* Controls */}
      <div style={styles.controls}>
        <div>
          <span style={styles.controlLabel}>Filter by Table: </span>
          <select
            style={styles.select}
            value={tableFilter}
            onChange={(e) => setTableFilter(e.target.value)}
            aria-label="Filter by table"
          >
            <option value="">All Tables</option>
            {tableNames.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
            <option value="__unassigned__">Unassigned</option>
          </select>
        </div>
        <div>
          <span style={styles.controlLabel}>Sort by: </span>
          <select
            style={styles.select}
            value={`${sortField}-${sortDir}`}
            onChange={(e) => {
              const [f, d] = e.target.value.split('-') as [SortField, SortDir];
              setSortField(f);
              setSortDir(d);
            }}
            aria-label="Sort order"
          >
            <option value="name-asc">Name A→Z</option>
            <option value="name-desc">Name Z→A</option>
            <option value="table-asc">Table (ascending)</option>
            <option value="table-desc">Table (descending)</option>
          </select>
        </div>
      </div>

      <p style={styles.count}>
        {displayed.length} {displayed.length === members.length ? 'members' : `of ${members.length} members`}
      </p>

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>#</th>
            <th style={styles.th} onClick={() => handleSort('name')}>Name{sortIndicator('name')}</th>
            <th style={styles.th}>Ticket</th>
            <th style={styles.th}>Chapter</th>
            <th style={styles.th} onClick={() => handleSort('table')}>Table{sortIndicator('table')}</th>
            <th style={styles.th}>Seat</th>
          </tr>
        </thead>
        <tbody>
          {displayed.map((m, idx) => (
            <tr key={m.id}>
              <td style={{ ...styles.td, color: TEXT_MUTED }}>{idx + 1}</td>
              <td style={{ ...styles.td, fontWeight: 600 }}>{m.fullName}</td>
              <td style={styles.td}>{m.ticketNumber || '—'}</td>
              <td style={styles.td}>{m.chapterAffiliation || '—'}</td>
              <td style={styles.td}>
                {m.tableName ? (
                  <span style={{ ...styles.badge, backgroundColor: '#e8f5e9', color: '#2e7d32' }}>{m.tableName}</span>
                ) : (
                  <span style={{ color: TEXT_MUTED }}>Unassigned</span>
                )}
              </td>
              <td style={styles.td}>{m.seatNumber || '—'}</td>
            </tr>
          ))}
          {displayed.length === 0 && (
            <tr>
              <td colSpan={6} style={{ ...styles.td, textAlign: 'center', color: TEXT_MUTED, padding: '2rem' }}>
                No members match the current filter.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
